import { createExportColumnHelper } from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import { DEAL_COLUMNS } from 'components/DataExpansionComponents/Deals/dealsColumnInfo'
import type { ExportModalColumn } from './ExportModal'

/**
 * Map V2 deals-table column ids to the export field ids the export modal lists.
 *
 * We always render the V2 deals table. Each column below overrides its
 * react-table `id` in `useDealsTableColumnsV2.tsx` (e.g. the company column is
 * `accessor('cc_id', { id: 'cc_company_name' })`), so `ColumnsStore.selectedColumns`
 * persists the override id (`cc_company_name`) while the export modal lists the
 * backend export field id (`company`). This map is exactly that set of `id:`
 * overrides, keyed by the override id (the value the store actually holds).
 *
 * Columns WITHOUT an `id` override already persist the export field id, so they
 * match directly and need no entry. We deliberately do NOT map raw accessor
 * keys such as `cc_id`: the store never holds them, and mapping a numeric
 * identity accessor onto a name field could wrongly pre-check a box if such a
 * column were ever surfaced.
 *
 * Affects only which boxes start checked; the exported `selected_columns` always
 * use export field ids, so an unmapped id can never corrupt an export.
 */
export const V2_COLUMN_ID_TO_EXPORT_ID: Record<string, string> = {
  cc_company_name: 'company',
  cc_category_name: 'category',
  cc_sub_category_name: 'sub_category',
  cc_distributors: 'distributors',
  cc_gpos: 'gpos',
  cc_note_count: 'note_count',
  cc_contact_count: 'contact_count',
  product_variant_name: 'product_variant',
  sales_stage_name: 'sales_stage',
  account_owner_name: 'account_owner',
}

/**
 * Deals columns offered for export, in display order. Grouping is intentionally
 * NOT defined here: each column's group is read from `DEAL_COLUMNS[id].group`
 * (dealsColumnInfo) — the same source the column modal groups by — so the export
 * modal and the column modal can never drift apart (FIR-7461).
 */
export const DEAL_EXPORT_COLUMN_IDS: (keyof typeof DEAL_COLUMNS)[] = [
  'company',
  'category',
  'sub_category',
  // "company_size" intentionally omitted: it duplicated "Deal Size" (doors_count)
  // for entire-company deals. Removed from the deals table + export. See FIR-7468.
  'taglist',
  'contact_tags',
  'distributors',
  'gpos',
  'note_count',
  'contact_count',
  'days_since_last_modified',
  'days_since_deal_last_modified',
  'estimated_meals_per_day',
  'operational_schools',
  'doors_count',
  'deal_name',
  'product',
  'product_variant',
  'sku',
  'account_owner',
  'sales_stage',
  'last_datetime_stage_changed',
  'expected_days_to_close',
  'close_date_override',
  'origin_campaign',
  'deal_taglist',
  'days_since_reply',
  'days_since_sent',
  'modified',
  'created',
  'monthly_volume_predicted',
  'monthly_volume_override',
  'monthly_revenue_predicted',
  'monthly_revenue_override',
  'annual_volume',
  'annual_revenue',
  'annual_brand_opp',
  'annual_total',
]

/** Preferred display order of the column groups in the export modal. */
export const DEAL_EXPORT_GROUP_ORDER: string[] = [
  'Company Attributes',
  'Campaigns and Deals',
  'Forecast',
]

/** Column ids only meaningful when the email integration is enabled. */
const EMAIL_ONLY_COLUMN_IDS = new Set(['days_since_reply', 'days_since_sent'])

/**
 * Build the grouped column structure for the deals export modal. Columns are
 * bucketed by their `DEAL_COLUMNS[id].group` (single source of truth shared with
 * the column modal), groups are rendered in `DEAL_EXPORT_GROUP_ORDER`, and any
 * group not listed there is appended afterwards rather than silently dropped.
 * Email-only metrics are excluded when the integration is disabled.
 */
export function buildDealExportColumns(opts: {
  enableEmailIntegration: boolean
}): ExportModalColumn[] {
  const helper = createExportColumnHelper({ columns: DEAL_COLUMNS })
  const ids = DEAL_EXPORT_COLUMN_IDS.filter(
    (id) => opts.enableEmailIntegration || !EMAIL_ONLY_COLUMN_IDS.has(id)
  )

  const byGroup = new Map<string, string[][]>()
  for (const id of ids) {
    const group = DEAL_COLUMNS[id].group
    const elements = byGroup.get(group) ?? []
    elements.push(helper.retrieveColumn(id) as string[])
    byGroup.set(group, elements)
  }

  const orderedGroups = [
    ...DEAL_EXPORT_GROUP_ORDER,
    ...[...byGroup.keys()].filter((g) => !DEAL_EXPORT_GROUP_ORDER.includes(g)),
  ]
  return orderedGroups
    .filter((group) => byGroup.has(group))
    .map((title) => ({ title, elements: byGroup.get(title) as string[][] }))
}
